import { useMixpanel } from '@/context/mixpanel'
import { useVaultsSortState } from '@/hooks/useVaultsSortState'
import type { VaultsSortSchema } from '@/types/vaultsSortSchema'
import { vaultsTableColumns } from '@/types/vaultsTableColumn'
import { cn } from '@/utils/cn'
import type { PopoverProps } from '@radix-ui/react-popover'
import { Button } from '../common/button'
import { Popover, PopoverContent, PopoverList, PopoverListItem, PopoverTrigger } from '../common/popover'
import { ScrollArea } from '../common/scrollArea'
import { SortIcon } from '../icons/sort'

interface SortSelectProps {
  className?: string
}

export const SortSelect = ({ className, modal }: SortSelectProps & Pick<PopoverProps, 'modal'>) => {
  const { sortState, handleSetSortBy } = useVaultsSortState()
  const { trackEvent } = useMixpanel()

  return (
    <Popover modal={modal}>
      <PopoverTrigger asChild>
        <Button
          variant="default"
          size="md"
          className={cn(
            'group flex items-center gap-2 rounded-xl border bg-surface px-3 text-left transition-colors',
            'hocus:border-accent hocus:text-body-foreground',
            className
          )}
        >
          <SortIcon size={16} className={cn('transition-transform', sortState.asc ? '-scale-y-100' : '')} />
          <span>Sort by {getSortTriggerName(sortState.sortBy)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <ScrollArea className="h-44">
          <PopoverList>
            {vaultsTableColumns.map((sort) => (
              <PopoverListItem key={sort} className="h-10 border-t">
                <Button
                  variant="default"
                  onClick={() => {
                    handleSetSortBy(sort)
                    trackEvent('Mobile sort used', { Sort: sort })
                  }}
                  className={cn(
                    'flex h-full w-full max-w-full justify-between truncate px-4',
                    'hocus-within:bg-surface'
                  )}
                >
                  <div className="flex w-full max-w-full items-center justify-between gap-2 truncate">
                    <span className="mr-2 block first-letter:uppercase">{getSortTriggerName(sort)}</span>
                    <div
                      className={cn(
                        'transition-transform',
                        sort === sortState.sortBy ? 'opacity-100' : 'opacity-0',
                        sortState.asc ? '-scale-y-100' : 'scale-y-100'
                      )}
                    >
                      <SortIcon />
                    </div>
                  </div>
                </Button>
              </PopoverListItem>
            ))}
          </PopoverList>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}

function getSortTriggerName(sort: VaultsSortSchema['sortBy']) {
  switch (sort) {
    case 'tvl':
      return sort.toUpperCase()
    case 'apy':
      return 'Yield'
    default:
      return sort
  }
}
